<script setup lang="ts">
import { computed, useAttrs } from 'vue';

defineOptions({ inheritAttrs: false, name: 'EGS_Input' });

type InputValue = string | number | null;
type InputSize = 'sm' | 'md' | 'lg' | 'small' | 'large' | string;
type InputMode = 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
type InputIcon = string | [string, string];

interface EgsInputProps {
	modelValue?: InputValue;
	value?: InputValue;
	type?: string;
	id?: string;
	inputId?: string;
	name?: string;
	placeholder?: string;
	autocomplete?: string;
	inputmode?: InputMode;
	min?: number | string;
	max?: number | string;
	step?: number | string;
	maxlength?: number | string;
	size?: InputSize;
	disabled?: boolean;
	readonly?: boolean;
	required?: boolean;
	invalid?: boolean;
	fluid?: boolean;
	inputClass?: unknown;
	prefix?: string;
	suffix?: string;
	currency?: string;
	mode?: string;
	locale?: string;
	minFractionDigits?: number;
	maxFractionDigits?: number;
	useGrouping?: boolean;
	icon?: InputIcon | null;
	autoResize?: boolean;
}

const props = withDefaults(defineProps<EgsInputProps>(), {
	modelValue: '',
	type: 'text',
	placeholder: '',
	size: 'md',
	disabled: false,
	readonly: false,
	required: false,
	invalid: false,
	fluid: false,
	prefix: '',
	suffix: '',
	currency: 'USD',
	mode: 'decimal',
	useGrouping: false,
	icon: null,
	autoResize: false
});
const emit = defineEmits<{
	'update:modelValue': [value: InputValue];
	input: [event: Event];
	change: [event: Event];
	blur: [event: FocusEvent];
	focus: [event: FocusEvent];
	keyup: [event: KeyboardEvent];
}>();
const attrs = useAttrs();
const isTextarea = computed(() => props.type === 'textarea');
const isNumeric = computed(() => props.type === 'number');
const hasAffix = computed(() => !isTextarea.value && Boolean(props.icon || props.prefix || props.suffix || props.mode === 'currency'));
const displayValue = computed(() => props.modelValue ?? props.value ?? '');
const resolvedPrefix = computed(() => props.prefix || (props.mode === 'currency' ? (props.currency === 'USD' ? '$' : props.currency) : ''));
const resolvedStep = computed(() => {
	if (props.step != null) return props.step;
	return props.maxFractionDigits && props.maxFractionDigits > 0 ? 1 / 10 ** props.maxFractionDigits : 1;
});
const sizeClass = computed(() => {
	if (props.size === 'sm' || props.size === 'small') return 'h-8 px-2.5 text-xs';
	if (props.size === 'lg' || props.size === 'large') return 'h-12 px-4 text-base';
	return 'h-10 px-3 text-sm';
});
const inputClasses = computed(() => [
	'egs-input block rounded-lg border bg-[var(--egs-content-background)] text-color outline-none transition placeholder:text-color-secondary',
	'focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200',
	'disabled:cursor-not-allowed disabled:bg-[var(--egs-content-hover)] disabled:text-color-secondary',
	isTextarea.value ? 'min-h-20 resize-y px-3 py-2' : sizeClass.value,
	props.fluid ? 'w-full' : '',
	props.invalid ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-surface hover:border-primary/60',
	props.inputClass,
	attrs['class']
]);
const wrappedInputClasses = computed(() => ['min-w-0 flex-1 border-0 bg-transparent text-color outline-none placeholder:text-color-secondary focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60', sizeClass.value, props.inputClass]);
const wrapperClasses = computed(() => [
	'egs-input inline-flex items-center rounded-lg border bg-[var(--egs-content-background)] shadow-sm focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-200',
	props.fluid ? 'w-full' : '',
	props.invalid ? 'border-red-400' : 'border-surface',
	props.disabled ? 'bg-[var(--egs-content-hover)] opacity-60' : '',
	attrs['class']
]);
const inputAttributes = computed<Record<string, string | number>>(() => {
	const values: Record<string, string | number> = {};
	const optionalValues = {
		id: props.inputId || props.id,
		name: props.name,
		autocomplete: props.autocomplete,
		inputmode: props.inputmode,
		min: props.min,
		max: props.max,
		step: isNumeric.value ? resolvedStep.value : props.step,
		maxlength: props.maxlength
	};
	for (const [key, value] of Object.entries(optionalValues)) if (value != null) values[key] = value;
	return values;
});

function handleInput(event: Event) {
	const element = event.target as HTMLInputElement | HTMLTextAreaElement;
	let value: InputValue = element.value;
	if (isNumeric.value) {
		value = element.value === '' ? null : (element as HTMLInputElement).valueAsNumber;
		if (typeof value === 'number' && !Number.isFinite(value)) value = null;
	}
	if (isTextarea.value && props.autoResize) {
		element.style.height = 'auto';
		element.style.height = `${element.scrollHeight}px`;
	}
	emit('update:modelValue', value);
	emit('input', event);
}
</script>

<template>
	<textarea
		v-if="isTextarea"
		v-bind="{ ...attrs, ...inputAttributes }"
		:value="displayValue"
		:placeholder="placeholder"
		:disabled="disabled"
		:readonly="readonly"
		:required="required"
		:aria-invalid="invalid"
		:class="inputClasses"
		@input="handleInput"
		@change="emit('change', $event)"
		@blur="emit('blur', $event)"
		@focus="emit('focus', $event)"
		@keyup="emit('keyup', $event)"
	/>
	<div v-if="hasAffix" v-bind="attrs" :class="wrapperClasses">
		<span v-if="icon" class="pl-3 text-sm text-color-secondary"><font-awesome-icon :icon="icon" /></span>
		<span v-if="resolvedPrefix" class="pl-3 text-sm text-color-secondary">{{ resolvedPrefix }}</span>
		<input
			v-bind="inputAttributes"
			:type="type"
			:value="displayValue"
			:placeholder="placeholder"
			:disabled="disabled"
			:readonly="readonly"
			:required="required"
			:aria-invalid="invalid"
			:class="wrappedInputClasses"
			@input="handleInput"
			@change="emit('change', $event)"
			@blur="emit('blur', $event)"
			@focus="emit('focus', $event)"
			@keyup="emit('keyup', $event)"
		/>
		<span v-if="suffix" class="pr-3 text-sm text-color-secondary">{{ suffix }}</span>
	</div>
	<input
		v-else-if="!isTextarea"
		v-bind="{ ...attrs, ...inputAttributes }"
		:type="type"
		:value="displayValue"
		:placeholder="placeholder"
		:disabled="disabled"
		:readonly="readonly"
		:required="required"
		:aria-invalid="invalid"
		:class="inputClasses"
		@input="handleInput"
		@change="emit('change', $event)"
		@blur="emit('blur', $event)"
		@focus="emit('focus', $event)"
		@keyup="emit('keyup', $event)"
	/>
</template>
